import {
  signIn,
  signOut,
  getToken,
  getAuthorization,
  getCanonicalId,
  getRecentAnonymousIds,
  getEventTargetInstance,
  getIdentity
} from '@cimpress-technology/authfront-sdk-core'

import { connectionMapping } from './utils/config'

import type { UserSignInOptions, UserSignOutPayload, AuthorizationInfo, UserIdentityEvent, UrlParam } from '../common/types'

import { buildRedirectUrl, buildWAuth, isEmployeeConnection, getWindow, transformAuthIdentityEventToUserIdentityEvent, trackSignIn, getWindowLocation, getHomepageUrl, checkIsInternalUser, removeCareAgentParam } from './utils/helper'

import { cookieTokenStorage } from './init'
import { EMPTY_IDENTITY, TOKEN_CLAIMS } from '../common/constants'

class AuthService {
  private static _eventTargetInstance: any | null = null

  static get eventTargetInstance (): EventTarget {
    if (!this._eventTargetInstance) {
      this._eventTargetInstance = getEventTargetInstance()
    }
    return this._eventTargetInstance!
  }

  isSignedIn = (): boolean => {
    try {
      const profile = getIdentity()?.profile as Record<string, unknown> | undefined
      if (!profile || Object.keys(profile).length === 0) return false

      return profile[TOKEN_CLAIMS.IS_ANONYMOUS_TOKEN_CLAIM] !== true
    } catch {
      return false
    }
  }

  getToken = (): string | null => {
    throw new Error('getToken is deprecated. Please use getAuthorizationHeader instead.')
  }

  getTokenType = (): string | null => {
    throw new Error('getTokenType is deprecated. Please use getAuthorizationHeader instead.')
  }

  getCanonicalId = (): string | null => {
    try {
      return getCanonicalId() || null
    } catch (error) {
      return null
    }
  }

  getProfile = (): object | null => {
    try {
      const identity = this.getIdentity()
      if (identity.profile && Object.keys(identity.profile).length > 0) return identity.profile
      return null
    } catch (error) {
      return null
    }
  }

  getIdentity = (): UserIdentityEvent => {
    try {
      const identity = getIdentity()
      if (!identity) return EMPTY_IDENTITY

      return {
        ...identity,
        isSignedIn: this.isSignedIn(),
        referenceToken: getToken() || null,
        recentAnonymousIds: this.getRecentAnonymousIds()!,
        authorizationHeader: this.getAuthorizationHeader()!,
      }
    } catch (error) {
      return EMPTY_IDENTITY
    }
  }

  signIn = (
    additionalReturnUrlQueryParams: UrlParam[] = [],
    additionalReturnUrlHashParams: UrlParam[] = [],
    options: UserSignInOptions = {}): void => {
    if (this.isSignedIn()) {
      return
    }

    const ubikAuthObject = getWindow().ubikAuthObject
    if (!ubikAuthObject) {
      throw new Error('Auth configuration is missing. Please initialize the authentication service with the appropriate configuration.')
    }
    const { clientId, culture, connection, options: configOption = {} } = ubikAuthObject

    const redirectUrl = buildRedirectUrl(getWindow().location, additionalReturnUrlQueryParams, additionalReturnUrlHashParams)
    const { pathname, search, hash } = new URL(redirectUrl)
    const postLoginRedirectToPage = pathname + search + hash

    const finalOptions = {
      ...configOption,
      ...options
    }

    cookieTokenStorage.setClientId(clientId)
    trackSignIn(options && options.trackingCaller, () => {
      if (connectionMapping[connection]) {
        const transformer = connectionMapping[connection]
        const { loginHint, additionalParams } = transformer ? transformer(finalOptions) : {}
        signIn({ postLoginRedirectToPage, connection, uiLocales: [culture], loginHint, anonIdentity: getToken(), additionalParams })
        return
      }

      // care connection waad does not require wauth param
      let wauth
      if (!isEmployeeConnection(connection)) {
        wauth = buildWAuth(finalOptions, redirectUrl, this.getCanonicalId(), getToken(), culture)
      }
      signIn({ postLoginRedirectToPage, connection, wauth })
    })
  }

  getAuthorizationHeader = (): string | null => {
    try {
      const header = this.getAuthorization()
      return header ? `${header.type} ${header.token}` : null
    } catch (error) {
      return null
    }
  }

  signOut = (option?: UserSignOutPayload): void => {
    if (!this.isSignedIn()) {
      return
    }
    const ubikAuthObject = getWindow().ubikAuthObject
    if (!ubikAuthObject) {
      throw new Error('Auth configuration is missing. Please initialize the authentication service with the appropriate configuration.')
    }
    const { clientId, culture, tenant } = ubikAuthObject

    const clientInConfig = cookieTokenStorage.getClientId()
    cookieTokenStorage.deleteClientId()
    cookieTokenStorage.deletePresumedLoginState()
    if (clientInConfig && clientInConfig !== clientId) {
      let returnTo = getWindowLocation().href
      if (checkIsInternalUser(this.getIdentity()!)) {
        returnTo = removeCareAgentParam(returnTo)
      }
      signOut({ returnTo, clientId: clientInConfig })
      return
    }
    if (option?.returnTo) {
      signOut({ returnTo: option.returnTo })
    } else {
      getHomepageUrl(tenant, culture).then((returnTo) => signOut({ returnTo }))
    }
  }

  getAuthorization = (): AuthorizationInfo | null => {
    try {
      return getAuthorization() || null
    } catch (error) {
      return null
    }
  }

  onUserIdentityUpdate = (callback: (result?: any) => void): void => {
    if (typeof callback !== 'function') { return }

    const authfrontContainer = getWindow().authfrontContainer

    if (authfrontContainer && authfrontContainer.configRegistrationStatus === 'complete') {
      callback(transformAuthIdentityEventToUserIdentityEvent(getIdentity()!))
    }

    // transform identity object to match vp auth identity object
    AuthService.eventTargetInstance.addEventListener('userIdentity', (e: any) => {
      const userDetails = e.detail
      callback(transformAuthIdentityEventToUserIdentityEvent(userDetails))
    })

    AuthService.eventTargetInstance.addEventListener('completeCallbackEvent', () => {
      callback(EMPTY_IDENTITY)
    })
  }

  getRecentAnonymousIds = (): string[] | null => getRecentAnonymousIds() || null
}

export { AuthService }
